import { useEffect, useRef, useState } from 'react'

/* -----------------------------------------------------------------------------
   THE BARS GET OUT OF THE WAY WHILE YOU READ (David, 12 Oct).

   Below 820px the topbar and the tab bar slide away when the page scrolls
   DOWN and come back on any real scroll UP — how feed apps behave now. This
   file decides WHEN; hub.css (`.shell--chrome-hidden`, in an 820px block)
   decides what hiding looks like, so above 820px the class changes nothing.

   ── THIS REVERSES PART OF SHL-18, DELIBERATELY ─────────────────────────────

   16 Sep fixed the tab bar to the viewport because a bar that scrolls away
   with the page is "present but unreachable". That is still true and still
   the rule: the bars never scroll WITH the page. What changed is that they
   step aside while you are reading forwards and return the moment you move
   back — one flick, wherever you are. Do not "fix" this back to always-on on
   the strength of SHL-18's comment; David chose it on 12 Oct.

   ── WHAT KEEPS THEM ON SCREEN ──────────────────────────────────────────────

   The top of the page (CHROME_TOP), and whatever the caller says is pinned:
   AppShell pins while the Ask One drawer or the tour is open, or while any
   control in the topbar has a menu open (aria-expanded="true" — the bell, the
   initials). Focus inside a bar shows it too, in CSS (`:focus-within`), so a
   keyboard never lands on something it cannot see.

   ── WHY A STEP FUNCTION ────────────────────────────────────────────────────

   `stepChrome` is pure so the suite can drive it with numbers; an effect does
   not run under renderToString. Movement is ACCUMULATED per direction, so a
   jittery thumb or iOS's rubber band at the bottom does not flicker the bars:
   it takes CHROME_DOWN px of continuous downward travel to hide and
   CHROME_UP px of continuous upward travel to show.
   -------------------------------------------------------------------------- */

export const CHROME_TOP = 80
export const CHROME_DOWN = 24
export const CHROME_UP = 12

export type ChromeState = { hidden: boolean; last: number; acc: number }

export function stepChrome(s: ChromeState, y: number, pinned: boolean): ChromeState {
  if (y <= CHROME_TOP || pinned) return { hidden: false, last: y, acc: 0 }
  const dy = y - s.last
  if (dy === 0) return s
  // A change of direction starts the count again.
  const acc = s.acc === 0 || dy > 0 === s.acc > 0 ? s.acc + dy : dy
  if (acc >= CHROME_DOWN) return { hidden: true, last: y, acc }
  if (acc <= -CHROME_UP) return { hidden: false, last: y, acc }
  return { hidden: s.hidden, last: y, acc }
}

/** True while the bars should be out of the way. Reads the window's scroll —
 *  the document scrolls, not `.hub` (the tab bar is fixed to the viewport). */
export function useHideOnScroll(isPinned: () => boolean): boolean {
  const [hidden, setHidden] = useState(false)
  const pinned = useRef(isPinned)
  pinned.current = isPinned

  useEffect(() => {
    if (typeof window === 'undefined') return
    let s: ChromeState = { hidden: false, last: window.scrollY, acc: 0 }
    let frame = 0
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        s = stepChrome(s, window.scrollY, pinned.current())
        setHidden(s.hidden)
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])

  return hidden
}
